import ScrollLock from './ScrollLock';
import { ScrollBar } from './ScrollBar';

/**
 * Filter Modal Component
 * Opens and closes the filter modal and handles filtering
 */
export class FilterModal {
  private modal: HTMLElement | null;
  private triggers: NodeListOf<HTMLElement>;
  private triggerFixed: HTMLElement | null;
  private triggerNormal: HTMLElement | null;
  private closeButton: HTMLElement | null;
  private clearModalButton: HTMLElement | null;
  private decideButton: HTMLElement | null;
  private sectionClearButtons: NodeListOf<HTMLElement>;
  private scrollLock: ScrollLock;
  private content: HTMLElement | null;
  private contentMain: HTMLElement | null;
  private contentInner: HTMLElement | null;
  private scrollBar: ScrollBar | null;
  private maxContentMainHeight: number;
  private contentPlusHeight: number;
  private mediaQuery: MediaQueryList;

  constructor() {
    this.modal = document.querySelector('.js-filter-modal');
    this.triggers = document.querySelectorAll('.js-filter-modal-trigger');
    this.triggerNormal = document.querySelector('.js-filter-modal-trigger--normal');
    this.triggerFixed = document.querySelector('.js-filter-modal-trigger--fixed');
    this.closeButton = this.modal?.querySelector('.ms-c-filter-modal__close') || null;
    this.decideButton = this.modal?.querySelector('.js-filter-modal-decide') || null;
    this.clearModalButton = this.modal?.querySelector('.js-filter-clear-modal') || null;
    this.sectionClearButtons = document.querySelectorAll('.js-filter-clear');
    this.scrollLock = ScrollLock.getInstance();
    this.content = this.modal?.querySelector('.js-filter-modal-content') || null;
    this.contentMain = this.modal?.querySelector('.js-filter-modal-content-main') || null;
    this.contentInner = this.modal?.querySelector('.js-filter-modal-content-inner') || null;
    this.scrollBar = null;
    this.maxContentMainHeight = 0;
    this.mediaQuery = window.matchMedia('(min-width: 841px)');
    this.contentPlusHeight = this.calculateContentPlusHeight();

    if (!this.modal || this.triggers.length === 0) {
      console.warn('[FilterModal] Required elements not found');
      return;
    }

    this.init();
  }

  /**
   * Calculate content plus height based on media query
   * @returns The calculated height value
   */
  private calculateContentPlusHeight(): number {
    if (this.mediaQuery.matches) {
      // PC (width > 840px)
      return 24 + 114; // 138
    } else {
      // SP (width <= 840px)
      return 20 + 120 + 20; // 160
    }
  }

  /**
   * Initialize
   */
  private init(): void {
    // Initialize custom scrollbar
    if (this.contentInner) {
      this.initScrollBar();
    }

    // Open modal on trigger click
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.openModal();
      });
    });

    // Setup scroll observer for fixed trigger
    if (this.triggerNormal && this.triggerFixed) {
      this.setupScrollObserver();
    }

    // Close modal on close button click
    this.closeButton?.addEventListener('click', () => {
      this.closeModal();
    });

    // Close modal on background click (outside content area)
    this.modal?.addEventListener('click', (e) => {
      const modalContent = this.modal?.querySelector('.js-filter-modal-content');
      // Close if clicked element is not the modal content and not inside it
      if (modalContent && !modalContent.contains(e.target as Node)) {
        this.closeModal();
      }
    });

    // Close modal on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.classList.contains('is-active')) {
        this.closeModal();
      }
    });

    // Apply filter on decide button click
    this.decideButton?.addEventListener('click', () => {
      this.applyFilter();
    });

    // Clear modal checkboxes on clear modal button click (no navigation)
    this.clearModalButton?.addEventListener('click', (e) => {
      console.log('[FilterModal] clearModalButton clicked');
      e.preventDefault();
      e.stopPropagation();
      console.log('[FilterModal] preventDefault and stopPropagation called');
      this.clearModalCheckboxes();
    });

    // Clear section filters on section clear button click
    this.sectionClearButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.clearSectionFilter();
      });
    });

    // Track max content main height on resize
    this.setupContentMainHeightTracking();
  }

  /**
   * Setup content main height tracking
   * Tracks the maximum height of contentMain on resize
   */
  private setupContentMainHeightTracking(): void {
    if (!this.contentMain) return;

    // Update max height function
    const updateMaxHeight = () => {
      if (!this.contentMain) return;

      // Update contentPlusHeight based on current media query
      this.contentPlusHeight = this.calculateContentPlusHeight();

      const currentHeight = this.contentMain.clientHeight;
      if (currentHeight > this.maxContentMainHeight) {
        this.maxContentMainHeight = currentHeight;
        // console.log('[FilterModal] Max contentMain height updated:', this.maxContentMainHeight);
        // console.log('[FilterModal] Content plus height:', this.contentPlusHeight);

        // Calculate content height using contentPlusHeight
        const contentHeight = this.maxContentMainHeight + this.contentPlusHeight;
        if (this.content?.style) {
          this.content.style.maxHeight = `${contentHeight}px`;
        }
      }
    };

    // Initial measurement
    updateMaxHeight();

    // Update on window resize
    window.addEventListener('resize', updateMaxHeight);

    // Watch for content size changes using ResizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateMaxHeight);
      resizeObserver.observe(this.contentMain);
    }
  }

  /**
   * Set z-index for ul-widget-main-window
   * @param zIndex - The z-index value to set
   */
  private setUlWidgetZIndex(zIndex: string): void {
    const ulWidget = document.querySelector('.ul-widget-main-window') as HTMLElement;
    if (ulWidget) {
      ulWidget.style.zIndex = zIndex;
    }
  }

  /**
   * Setup scroll observer for fixed trigger
   * Shows fixed trigger when normal trigger is scrolled out of view
   */
  private setupScrollObserver(): void {
    if (!this.triggerNormal || !this.triggerFixed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Normal trigger is visible - hide fixed trigger
            this.triggerFixed?.classList.remove('is-active');
          } else {
            // Normal trigger is hidden - show fixed trigger
            this.triggerFixed?.classList.add('is-active');
          }
        });
      },
      {
        // Trigger when any part of the element goes out of viewport
        threshold: 0,
        rootMargin: '0px'
      }
    );

    observer.observe(this.triggerNormal);
  }

  /**
   * Initialize custom scrollbar
   */
  private initScrollBar(): void {
    if (!this.contentInner) return;

    // Get existing scrollbar container
    const scrollBarContainer = this.modal?.querySelector('.js-scroll-bar') as HTMLElement;

    if (!scrollBarContainer) {
      console.warn('[FilterModal] .js-scroll-bar not found');
      return;
    }

    // Initialize ScrollBar with existing element
    this.scrollBar = new ScrollBar(scrollBarContainer);

    // Update scrollbar when content scrolls
    this.contentInner.addEventListener('scroll', () => {
      this.updateScrollBar();
    });

    // Listen to scrollbar changes and update content scroll
    scrollBarContainer.addEventListener('scrollbar:change', ((e: CustomEvent) => {
      if (this.contentInner) {
        this.contentInner.scrollTop = e.detail.scrollTop;
      }
    }) as EventListener);

    // Initial update
    this.updateScrollBar();

    // Update scrollbar on window resize
    window.addEventListener('resize', () => {
      this.updateScrollBar();
    });

    // Watch for content size changes using ResizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        this.updateScrollBar();
      });
      resizeObserver.observe(this.contentInner);
    }

    // Watch for DOM changes (tab switches, dynamic content)
    if (typeof MutationObserver !== 'undefined') {
      const mutationObserver = new MutationObserver(() => {
        this.updateScrollBar();
      });
      mutationObserver.observe(this.contentInner, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }
  }

  /**
   * Update scrollbar based on content scroll
   */
  private updateScrollBar(): void {
    if (!this.contentInner || !this.scrollBar) return;

    this.scrollBar.updateScroll(
      this.contentInner.scrollHeight,
      this.contentInner.clientHeight,
      this.contentInner.scrollTop
    );
  }

  /**
   * Open modal
   */
  private openModal(): void {
    this.modal?.classList.add('is-active');
    this.scrollLock.disable();

    // Set z-index for ul-widget-main-window to 100
    this.setUlWidgetZIndex('100');

    // Update scrollbar when modal opens (content might have changed)
    setTimeout(() => {
      this.updateScrollBar();
    }, 100);
  }

  /**
   * Close modal
   */
  private closeModal(): void {
    // Remove is-active class first to start the CSS transition
    this.modal?.classList.remove('is-active');

    // Wait for the transition to complete before re-enabling scroll
    // This prevents scroll position jumping during the fade-out animation
    setTimeout(() => {
      this.scrollLock.enable();
      // Reset z-index for ul-widget-main-window to original value
      this.setUlWidgetZIndex('2147483647');
    }, 400); // Match the CSS transition duration (0.4s)
  }

  /**
   * Apply filter and navigate to filtered URL
   */
  private applyFilter(): void {
    const params = new URLSearchParams();

    // Get active tab (color or taste)
    const activeTab = this.modal?.querySelector('.ms-c-color-taste__tab--active') as HTMLElement;
    const activeTabType = activeTab?.dataset.tab;

    // Get checked checkboxes based on active tab
    if (activeTabType === 'color') {
      // Get selected colors
      const colorCheckboxes = this.modal?.querySelectorAll('input[name="color"]:checked') as NodeListOf<HTMLInputElement>;
      const colorValues: string[] = [];
      colorCheckboxes.forEach(checkbox => {
        colorValues.push(checkbox.value);
      });
      if (colorValues.length > 0) {
        params.append('color', colorValues.join(','));
      }
    } else if (activeTabType === 'taste') {
      // Get selected tastes
      const tasteCheckboxes = this.modal?.querySelectorAll('input[name="taste"]:checked') as NodeListOf<HTMLInputElement>;
      const tasteValues: string[] = [];
      tasteCheckboxes.forEach(checkbox => {
        tasteValues.push(checkbox.value);
      });
      if (tasteValues.length > 0) {
        params.append('taste', tasteValues.join(','));
      }
    }

    // Get selected layouts (always apply regardless of tab)
    const layoutCheckboxes = this.modal?.querySelectorAll('input[name="layout"]:checked') as NodeListOf<HTMLInputElement>;
    const layoutValues: string[] = [];
    layoutCheckboxes.forEach(checkbox => {
      layoutValues.push(checkbox.value);
    });
    if (layoutValues.length > 0) {
      params.append('layout', layoutValues.join(','));
    }

    // Get selected state1 (building type - always apply regardless of tab)
    const state1Checkboxes = this.modal?.querySelectorAll('input[name="state1"]:checked') as NodeListOf<HTMLInputElement>;
    const state1Values: string[] = [];
    state1Checkboxes.forEach(checkbox => {
      state1Values.push(checkbox.value);
    });
    if (state1Values.length > 0) {
      params.append('state1', state1Values.join(','));
    }

    // Navigate to filtered URL
    const queryString = params.toString();
    const url = queryString ? `?${queryString}` : window.location.pathname;
    window.location.href = url;
  }

  /**
   * Clear section-specific filters
   * Navigate to gallery page without query params
   */
  private clearSectionFilter(): void {
    // Navigate to URL without query params
    window.location.href = window.location.pathname;
  }

  /**
   * Clear modal checkboxes only (without navigation)
   */
  private clearModalCheckboxes(): void {
    // Uncheck all checkboxes in the modal
    const allCheckboxes = this.modal?.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
    allCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new FilterModal();
});
